'use client';

import { classes } from '@/next-utils/css-utils/scss-utils';
import { observer } from 'mobx-react-lite';
import dynamic from 'next/dynamic';
import { usePathname } from 'next/navigation';
import { useCallback, useState } from 'react';
import { useRevalidateGlobalCart } from '@/react/providers/global/RevalidateGlobalCartContext';
import { useGlobalContext } from '../../../hooks/useGlobalContext';
import { CommonErrorBoundary } from '../../errors/boundaries/CommonErrorBoundary';
import CartButton from '../../navigation/Header/components/CartButton';
import { HoverIntent } from '../../utility/HoverIntent';

import S from './styles.base.module.scss';

const MiniCart = dynamic(
  async () => import('../MiniCart').then((mod) => mod.MiniCart),
  { ssr: false }
);

/**
 * Mini Cart Button used to control the Mini Cart drop down in the header.
 */
export const MiniCartButton = observer(function MiniCartButton() {
  const [openMiniCart, setOpenMiniCart] = useState(false);

  const { cart } = useGlobalContext();
  const revalidateGlobalCart = useRevalidateGlobalCart();

  const handleOpenCart = useCallback(() => setOpenMiniCart(true), []);
  const handleCloseCart = useCallback(() => setOpenMiniCart(false), []);

  const canShowMiniCart = cart.totalProductQuantity > 0;
  const shouldOpenCart = openMiniCart && canShowMiniCart;

  // Check to see if the current page is the cart page.
  /* eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion -- Next complains that this
  hook may return null for fallback routes, which are deprecated and unused. */
  const pathname = usePathname()!;
  const isCartPage = pathname.endsWith('/cart');

  return (
    <HoverIntent
      onHoverIn={handleOpenCart}
      onHoverOut={handleCloseCart}
      disabled={isCartPage}
    >
      <div className={S.container}>
        <CartButton count={cart.totalProductQuantity} />
        {canShowMiniCart && (
          <div
            className={classes(
              S.miniCart,
              shouldOpenCart ? S.visible : S.hidden
            )}
            aria-hidden={!shouldOpenCart}
            // For accessibility, this must remain non-interactive (inert)
            // when the the cart popup is closed.
            inert={!shouldOpenCart}
          >
            <CommonErrorBoundary fallback={null}>
              {/* Always render the MiniCart component, even if the popup
              is closed. This is to prevent the internal PayPal button from
              unmounting and aborting a payment request. */}
              <MiniCart
                cart={cart}
                completeCheckoutCart={revalidateGlobalCart}
              />
            </CommonErrorBoundary>
          </div>
        )}
      </div>
    </HoverIntent>
  );
});
